const _ = require("lodash");
const browser = require("detect-browser").detect();
const ErrorUtil = require("../../../shared/ErrorUtil");

const noopStorage = {
	getItem: () => null,
	setItem: () => {},
	removeItem: () => {},
	clear: () => {},
};

let storage;

// Wraps all storage actions in a try-catch.
const tryStorageAction = function(f, options) {
	const {defaultReturn, methodName = "", args = []} = options || {};
	try {
		return f();
	} catch (ex) {
		ErrorUtil.log(
			"E_STORAGE_ACTION_FAILED",
			`Storage action "${methodName}" failed. Might be because localStorage does not exist?`,
			{
				methodName,
				args,
				exception: ex,
				storage: global.localStorage
					? {
							length: global.localStorage.length,
							keys: _.mapValues(global.localStorage, "length"),
					  }
					: {length: undefined, keys: {}},
			}
		);
		storage = noopStorage;
	}
	return defaultReturn;
};

// This returns a genuine localStorage interface (getItem, setItem, removeItem,
// clear only) if supported. Else it replaces each function with a noop.
const getStorage = function() {
	if (process.env.IS_NODE || !global.localStorage) {
		return noopStorage;
	}
	if (storage) {
		return storage;
	}

	storage = {};
	storage.getItem = k =>
		tryStorageAction(() => global.localStorage.getItem(k), {
			defaultReturn: null,
			methodName: "getItem",
			args: [k],
		});
	storage.setItem = (k, v) =>
		tryStorageAction(() => global.localStorage.setItem(k, v), {
			methodName: "setItem",
			args: [k, v],
		});
	storage.removeItem = k =>
		tryStorageAction(() => global.localStorage.removeItem(k), {
			methodName: "removeItem",
			args: [k],
		});
	storage.clear = () =>
		tryStorageAction(() => global.localStorage.clear(), {methodName: "clear"});

	// If localStorage does not exist or is not allowed by the browser, these
	// lines will throw in tryStorageAction and replace the storage variable with
	// a noop. Borrowed from modernizr:
	// http://stackoverflow.com/questions/11214404/how-to-detect-if-browser-supports-html5-local-storage
	const key = "baonline";
	storage.setItem(key, key);
	storage.removeItem(key);
	return storage;
};

// Returns whether the storage is a noop that is not actually storing anything.
const isDisabled = function() {
	return getStorage() === noopStorage;
};

// Browser bug detection function that determines whether a storage event fires
// on the tab that made the change.
// T11426: IE sometimes passes this check erroneously, so force the issue.
let eventFired = browser && browser.name === "ie";
(function() {
	if (eventFired || !global.window || isDisabled()) {
		return;
	}
	const now = String(Date.now());
	const storageListener = ev => {
		if (ev.key === "bugDetect" && ev.newValue === now) {
			eventFired = true;
		}
	};
	global.addEventListener("storage", storageListener);
	getStorage().setItem("bugDetect", now);
	// Browsers like IE11 that have this bug fire the event asynchronously, so
	// allow some time for the handler to fire. Empirical testing had a ~118ms
	// delay.
	setTimeout(() => {
		getStorage().removeItem("bugDetect");
		global.removeEventListener("storage", storageListener);
	}, 400);
})();

const firesEventOnOwnTab = () => eventFired;

module.exports = {
	getStorage,
	isDisabled,
	firesEventOnOwnTab,
};
